import styled, { keyframes, css } from 'styled-components'

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(166, 193, 11, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(166, 193, 11, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(166, 193, 11, 0);
  }
`

const mapExpand = keyframes`
  0% {
    opacity: 0.6;
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`

const mapShrink = keyframes`
  0% {
    opacity: 0.6;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`

export const MapContainer = styled.div<{ $isFullscreen?: boolean; $isShrinking?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 700px;
  background: rgba(18, 24, 38, 0.45);
  backdrop-filter: blur(12px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 15px 35px -10px rgba(0, 0, 0, 0.5);
  transition: min-height 0.3s ease, border-radius 0.3s ease;

  ${({ $isShrinking }) =>
    $isShrinking &&
    css`
      animation: ${mapShrink} 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    `}

  ${({ $isFullscreen }) =>
    $isFullscreen &&
    css`
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      max-width: 100vw !important;
      max-height: 100vh !important;
      min-height: 100vh !important;
      z-index: 99999 !important;
      border-radius: 0 !important;
      background: rgba(10, 15, 26, 0.97) !important;
      backdrop-filter: blur(24px) !important;
      border: none !important;
      box-shadow: none !important;
      animation: ${mapExpand} 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    `}

  .rsm-svg {
    width: 100%;
    height: 100%;
    flex: 1;
    min-height: ${({ $isFullscreen }) => ($isFullscreen ? 'calc(100vh - 75px)' : '620px')};
    outline: none;
    display: block;
  }
`

export const MinimizeButton = styled.button`
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.35);
  color: #f87171;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.25);
    border-color: rgba(239, 68, 68, 0.5);
    color: #ffffff;
    transform: translateY(-1px);
  }

  svg {
    width: 13px !important;
    height: 13px !important;
  }
`

export const FullscreenButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #94a3b8;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    border-color: rgba(255, 255, 255, 0.25);
  }

  svg {
    width: 13px !important;
    height: 13px !important;
  }
`

export const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  gap: 12px;
  flex-wrap: wrap;
  z-index: 10;

  svg {
    width: 13px !important;
    height: 13px !important;
    min-height: 0 !important;
    max-height: 13px !important;
    display: inline-block !important;
  }

  @media (min-width: 1024px) {
    flex-wrap: nowrap;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

export const KpiGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

export const KpiBadge = styled.div<{ $highlight?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${(props) =>
    props.$highlight ? 'rgba(166, 193, 11, 0.12)' : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid
    ${(props) => (props.$highlight ? 'rgba(166, 193, 11, 0.35)' : 'rgba(255, 255, 255, 0.07)')};
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 11px;
  color: #e2e8f0;

  strong {
    color: ${(props) => (props.$highlight ? '#a6c10b' : '#38bdf8')};
    font-weight: 700;
  }

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(props) => (props.$highlight ? '#a6c10b' : '#38bdf8')};
    box-shadow: 0 0 6px ${(props) => (props.$highlight ? '#a6c10b' : '#38bdf8')};
  }
`

export const RegionSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(0, 0, 0, 0.25);
  padding: 4px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  flex-wrap: wrap;
`

export const RegionButton = styled.button<{ $active: boolean }>`
  background: ${(props) => (props.$active ? '#a6c10b' : 'transparent')};
  color: ${(props) => (props.$active ? '#0f172a' : '#94a3b8')};
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: ${(props) => (props.$active ? '700' : '500')};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${(props) => (props.$active ? '0 0 10px rgba(166, 193, 11, 0.4)' : 'none')};

  &:hover {
    color: ${(props) => (props.$active ? '#0f172a' : '#ffffff')};
    background: ${(props) => (props.$active ? '#a6c10b' : 'rgba(255, 255, 255, 0.08)')};
  }
`

export const FloatingZoomControls = styled.div`
  position: absolute;
  right: 18px;
  top: 75px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 10;
`

export const ZoomBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 15px !important;
    height: 15px !important;
    min-height: 0 !important;
    max-height: 15px !important;
  }

  &:hover {
    background: rgba(166, 193, 11, 0.2);
    border-color: #a6c10b;
    color: #a6c10b;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(166, 193, 11, 0.25);
  }

  &:active {
    transform: translateY(0);
  }
`

export const TooltipContainer = styled.div<{ $x: number; $y: number; $visible: boolean }>`
  position: absolute;
  left: ${(props) => props.$x}px;
  top: ${(props) => props.$y}px;
  transform: ${(props) =>
    props.$y < 210 ? 'translate(-50%, 15px)' : 'translate(-50%, calc(-100% - 15px))'};
  pointer-events: none;
  z-index: 100;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
  transition: opacity 0.12s ease, transform 0.12s ease;
  width: 250px;
  background: rgba(15, 23, 42, 0.96);
  backdrop-filter: blur(14px);
  border: 1px solid rgba(166, 193, 11, 0.4);
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.7), 0 0 20px rgba(166, 193, 11, 0.15);

  svg {
    width: 13px !important;
    height: 13px !important;
    min-height: 0 !important;
    max-height: 13px !important;
    display: inline-block !important;
  }
`

export const TooltipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding-bottom: 6px;

  .title {
    font-size: 13px;
    font-weight: 700;
    color: #ffffff;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .region-tag {
    font-size: 9px;
    background: rgba(255, 255, 255, 0.08);
    color: #94a3b8;
    padding: 2px 6px;
    border-radius: 4px;
    text-transform: uppercase;
    font-weight: 600;
  }
`

export const TooltipBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  .metric-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;

    .label {
      font-size: 11px;
      color: #94a3b8;
    }

    .value {
      font-size: 14px;
      font-weight: 700;
      color: #a6c10b;
    }
  }

  .share-progress {
    width: 100%;
    height: 7px;
    background: rgba(255, 255, 255, 0.16);
    border-radius: 4px;
    overflow: hidden;
    margin-top: 4px;

    .fill {
      height: 100%;
      border-radius: 4px;
      transition: width 0.3s ease;
    }
  }

  .share-text {
    font-size: 10px;
    color: #94a3b8;
    text-align: right;
  }
`

export const TooltipFooter = styled.div`
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed rgba(255, 255, 255, 0.06);
  font-size: 10px;
  color: #38bdf8;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
`

export const LegendBox = styled.div`
  position: absolute;
  bottom: 14px;
  left: 18px;
  background: rgba(15, 23, 42, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  z-index: 20;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.5);

  .title {
    font-size: 10px;
    color: #94a3b8;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`

export const PulsingBadge = styled.div`
  width: 8px;
  height: 8px;
  background: #a6c10b;
  border-radius: 50%;
  animation: ${pulseAnimation} 1.8s infinite;
`

export const NorthSouthCard = styled.div`
  position: absolute;
  bottom: 60px;
  left: 18px;
  background: rgba(15, 23, 42, 0.94);
  backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 20;
  width: 270px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);

  .ns-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    color: #e2e8f0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding-bottom: 6px;
  }

  .ns-bars {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .ns-row {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.04);
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(56, 189, 248, 0.3);
      transform: translateX(2px);
    }

    .ns-track {
      width: 100%;
      height: 5px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 3px;
      overflow: hidden;
    }

    .ns-fill {
      height: 100%;
      border-radius: 3px;
      transition: width 0.3s ease;
    }
  }
`

export const StateSidePanel = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 55px;
  right: 14px;
  bottom: 14px;
  width: 660px;
  max-width: calc(100% - 28px);
  background: rgba(10, 16, 30, 0.96);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  box-shadow: -12px 12px 48px rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  z-index: 40;
  overflow: hidden;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease;
  transform: ${(props) => (props.$open ? 'translateX(0)' : 'translateX(calc(100% + 30px))')};
  opacity: ${(props) => (props.$open ? 1 : 0)};
  pointer-events: ${(props) => (props.$open ? 'all' : 'none')};

  @media (min-width: 1024px) {
    top: 58px;
    right: 16px;
    bottom: 16px;
    width: clamp(520px, 43vw, 680px);
    max-width: 48vw;
  }
`

export const PanelToggleBtn = styled.button`
  position: absolute;
  top: 65px;
  right: 18px;
  background: rgba(166, 193, 11, 0.18);
  border: 1px solid rgba(166, 193, 11, 0.4);
  color: #a6c10b;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  z-index: 30;
  backdrop-filter: blur(8px);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(166, 193, 11, 0.28);
    transform: translateY(-1px);
  }
`

export const PanelHeader = styled.div`
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;

  .title-group {
    display: flex;
    flex-direction: column;
    gap: 2px;

    h3 {
      font-size: 14px;
      font-weight: 700;
      color: #ffffff;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .sub {
      font-size: 11px;
      color: #94a3b8;
    }
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }
`

export const PanelTabs = styled.div`
  display: flex;
  background: rgba(0, 0, 0, 0.3);
  padding: 4px;
  gap: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`

export const PanelTabBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  background: ${(props) => (props.$active ? '#a6c10b' : 'transparent')};
  color: ${(props) => (props.$active ? '#0f172a' : '#94a3b8')};
  font-weight: ${(props) => (props.$active ? '700' : '500')};
  font-size: 11px;
  border: none;
  border-radius: 6px;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${(props) => (props.$active ? '#0f172a' : '#ffffff')};
  }
`

export const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
  }
`

export const PanelKpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`

export const PanelKpiItem = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 3px;

  .label {
    font-size: 9px;
    color: #94a3b8;
    text-transform: uppercase;
    font-weight: 600;
  }

  .val {
    font-size: 13px;
    font-weight: 700;
    color: #f8fafc;
  }

  .highlight {
    color: #a6c10b;
  }
`

export const PanelRegionBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 10px 16px;
  background: rgba(10, 16, 30, 0.75);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }
`

export const PanelRegionPillBtn = styled.button<{ $active: boolean; $color?: string }>`
  background: ${(props) =>
    props.$active
      ? props.$color
        ? `rgba(${
            props.$color === '#10b981'
              ? '16, 185, 129'
              : props.$color === '#eab308' || props.$color === '#84cc16'
              ? '234, 179, 8'
              : props.$color === '#0ea5e9'
              ? '14, 165, 233'
              : '56, 189, 248'
          }, 0.16)`
        : 'rgba(56, 189, 248, 0.16)'
      : 'rgba(255, 255, 255, 0.03)'};
  color: ${(props) => (props.$active ? '#ffffff' : '#94a3b8')};
  font-weight: ${(props) => (props.$active ? '700' : '500')};
  font-size: 11px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? props.$color || '#38bdf8'
        : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 8px;
  padding: 8px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: ${(props) =>
    props.$active
      ? `0 0 14px ${props.$color ? `${props.$color}33` : 'rgba(56, 189, 248, 0.25)'}`
      : 'none'};

  &:hover {
    background: ${(props) =>
      props.$active
        ? props.$color
          ? `rgba(${
              props.$color === '#10b981'
                ? '16, 185, 129'
                : props.$color === '#eab308' || props.$color === '#84cc16'
                ? '234, 179, 8'
                : props.$color === '#0ea5e9'
                ? '14, 165, 233'
                : '56, 189, 248'
            }, 0.22)`
          : 'rgba(56, 189, 248, 0.22)'
        : 'rgba(255, 255, 255, 0.07)'};
    border-color: ${(props) =>
      props.$active ? props.$color || '#38bdf8' : 'rgba(255, 255, 255, 0.18)'};
    color: #ffffff;
    transform: translateY(-1px);
  }

  .pill-title {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 700;
    color: ${(props) => (props.$active ? '#ffffff' : '#cbd5e1')};
  }

  .pill-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(props) => props.$color || '#38bdf8'};
  }

  .pill-sub {
    font-size: 9.5px;
    font-weight: 500;
    color: ${(props) => (props.$active ? '#cbd5e1' : '#64748b')};
    white-space: nowrap;
  }
`

export const ActionRecommendationCard = styled.div<{ $status: 'danger' | 'warning' | 'success' }>`
  background: ${(props) =>
    props.$status === 'danger'
      ? 'rgba(239, 68, 68, 0.08)'
      : props.$status === 'warning'
      ? 'rgba(245, 158, 11, 0.08)'
      : 'rgba(16, 185, 129, 0.08)'};
  border: 1px solid
    ${(props) =>
      props.$status === 'danger'
        ? 'rgba(239, 68, 68, 0.35)'
        : props.$status === 'warning'
        ? 'rgba(245, 158, 11, 0.35)'
        : 'rgba(16, 185, 129, 0.35)'};
  border-radius: 12px;
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .rec-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    .title {
      font-size: 13px;
      font-weight: 700;
      color: ${(props) =>
        props.$status === 'danger'
          ? '#f87171'
          : props.$status === 'warning'
          ? '#facc15'
          : '#4ade80'};
      display: flex;
      align-items: center;
      gap: 7px;
    }

    .badge {
      font-size: 11px;
      font-weight: 700;
      padding: 3px 10px;
      border-radius: 6px;
      background: ${(props) =>
        props.$status === 'danger'
          ? 'rgba(239, 68, 68, 0.22)'
          : props.$status === 'warning'
          ? 'rgba(245, 158, 11, 0.22)'
          : 'rgba(16, 185, 129, 0.22)'};
      color: ${(props) =>
        props.$status === 'danger'
          ? '#fca5a5'
          : props.$status === 'warning'
          ? '#fde047'
          : '#86efac'};
    }
  }

  .rec-body {
    font-size: 12px;
    color: #e2e8f0;
    line-height: 1.55;
  }
`

export const DemographicKpiBox = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .demo-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .demo-title {
      font-size: 13px;
      font-weight: 700;
      color: #38bdf8;
      display: flex;
      align-items: center;
      gap: 7px;
    }

    .status-pill {
      font-size: 11px;
      font-weight: 700;
      padding: 3px 10px;
      border-radius: 6px;
    }
  }

  .demo-metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;

    .item {
      display: flex;
      flex-direction: column;
      gap: 3px;

      .item-label {
        font-size: 10px;
        color: #94a3b8;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }

      .item-val {
        font-size: 15px;
        font-weight: 700;
        color: #ffffff;
      }
    }
  }

  .progress-wrap {
    display: flex;
    flex-direction: column;
    gap: 5px;

    .bar-track {
      position: relative;
      height: 9px;
      background: rgba(255, 255, 255, 0.08);
      border-radius: 5px;
      overflow: hidden;

      .bar-fill {
        height: 100%;
        border-radius: 5px;
        transition: width 0.4s ease;
      }

      .target-marker {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 70%;
        width: 3px;
        background: #facc15;
        z-index: 2;
        box-shadow: 0 0 6px rgba(250, 204, 21, 0.8);
      }
    }

    .bar-legend {
      display: flex;
      justify-content: space-between;
      font-size: 10.5px;
      color: #94a3b8;

      .marker-label {
        color: #facc15;
        font-weight: 700;
      }
    }
  }

  .demo-secondary-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);

    .sub-item {
      display: flex;
      flex-direction: column;
      gap: 2px;

      .sub-lbl {
        font-size: 10px;
        color: #94a3b8;
        font-weight: 600;
        text-transform: uppercase;
      }

      .sub-val {
        font-size: 13px;
        font-weight: 700;
        color: #f8fafc;
      }
    }
  }
`

export const CityMacroBadge = styled.span<{ $region: 'Norte' | 'Centro' | 'Sul' }>`
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 5px;
  background: ${(props) =>
    props.$region === 'Norte'
      ? 'rgba(16, 185, 129, 0.18)'
      : props.$region === 'Centro'
      ? 'rgba(132, 204, 22, 0.18)'
      : 'rgba(56, 189, 248, 0.18)'};
  color: ${(props) =>
    props.$region === 'Norte'
      ? '#34d399'
      : props.$region === 'Centro'
      ? '#a3e635'
      : '#38bdf8'};
  border: 1px solid
    ${(props) =>
      props.$region === 'Norte'
        ? 'rgba(16, 185, 129, 0.4)'
        : props.$region === 'Centro'
        ? 'rgba(132, 204, 22, 0.4)'
        : 'rgba(56, 189, 248, 0.4)'};
`

export const CityCardItem = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.055);
    border-color: rgba(166, 193, 11, 0.35);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
    transform: translateY(-1px);
  }

  .city-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;

    .city-name-group {
      display: flex;
      align-items: center;
      gap: 9px;

      .city-title {
        font-size: 14.5px;
        font-weight: 700;
        color: #ffffff;
      }
    }

    .city-vol-badge {
      font-size: 12.5px;
      font-weight: 700;
      color: #a6c10b;
      background: rgba(166, 193, 11, 0.1);
      padding: 3px 10px;
      border-radius: 6px;
      border: 1px solid rgba(166, 193, 11, 0.25);
      white-space: nowrap;
    }
  }

  .city-metrics-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    background: rgba(0, 0, 0, 0.3);
    padding: 10px 14px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.04);

    .stat-col {
      display: flex;
      flex-direction: column;
      gap: 3px;

      .stat-lbl {
        font-size: 9.5px;
        color: #94a3b8;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }

      .stat-val {
        font-size: 12.5px;
        font-weight: 700;
        color: #f8fafc;
      }
    }
  }

  .city-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;

    .city-meta {
      font-size: 11px;
      color: #94a3b8;
    }

    .city-actions {
      display: flex;
      align-items: center;
      gap: 8px;

      .btn-city-details {
        background: rgba(56, 189, 248, 0.15);
        border: 1px solid rgba(56, 189, 248, 0.4);
        color: #38bdf8;
        border-radius: 6px;
        padding: 6px 12px;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s ease;

        &:hover {
          background: rgba(56, 189, 248, 0.25);
          color: #ffffff;
          transform: translateY(-1px);
        }
      }
    }
  }
`


